<template>
  <div class="template">
    <div
      ref="headerGridRef"
      :class="hasSearchSlot ? 'header-grid' : 'header-grid-without-search'"
    >
      <HeaderBar :class="{ 'header-bar': hasSearchSlot }" />
      <div class="search-slot">
        <slot name="search"></slot>
      </div>
    </div>
    <slot></slot>

    <HeaderMobileNavBar v-if="isMobileScreen" />
  </div>
</template>

<script setup>
import { useBreakpointHelper } from '@/composables/breakpointHelper';

const { isBelowCurrentBreakpoint, windowHeight } = useBreakpointHelper();

/** @type {import('vue').ComputedRef<boolean>} Whether the viewport is below the `tw-md` (768px) breakpoint */
const isMobileScreen = computed(() => isBelowCurrentBreakpoint('tw-md'));

const slots = useSlots();

/** @type {import('vue').ComputedRef<boolean>} Whether the parent page provides a `search` slot, used to toggle the header grid layout */
const hasSearchSlot = computed(() => !!slots.search);

/** @type {import('vue').Ref<HTMLElement | null>} Template ref for the header grid container */
const headerGridRef = ref(null);

/**
 * Measures the header grid element and sets the `--header-grid-height` CSS custom
 * property on the document root. Fixed-position children (e.g. SavedSearch SideBar)
 * use this variable for their `top` offset and viewport-relative height calculation.
 */
const updateHeaderHeight = () => {
  if (headerGridRef.value) {
    const height = headerGridRef.value.offsetHeight;
    document.documentElement.style.setProperty('--header-grid-height', `${height}px`);
  }
};

onMounted(() => {
  updateHeaderHeight();
});

onUpdated(() => {
  updateHeaderHeight();
});

watch(windowHeight, () => {
  updateHeaderHeight();
});
</script>

<style lang="scss" scoped>
@use '@/assets/scss/container.scss';

.header-grid {
  @apply tw-contents;

  @screen tw-md {
    @apply tw-sticky tw-top-0 tw-z-40 tw-block;
  }
}

.header-grid-without-search {
  @apply tw-sticky tw-top-0 tw-z-50;
}

.header-bar,
.search-slot {
  @apply tw-md:tw-relative;
}

@media screen and (max-width: 767px) {
  .search-slot {
    @apply tw-sticky tw-top-0 tw-z-50;
  }
}
</style>
